import { CompositeLayer } from '@deck.gl/core';
import { debounce } from 'lodash';
import round from 'lodash/round';
import ClusterIndexManager from './ClusterIndexManager';

export default class BaseClusterCompositeLayer extends CompositeLayer {
  constructor(propObjects, layerName) {
    super(propObjects);

    this.clusterIndex = ClusterIndexManager.getInstance(layerName, propObjects);
    this.debouncedUpdate = debounce(this.performUpdate.bind(this), 150);
  }

  initializeState(context) {
    super.initializeState(context);

    this.setState({
      data: [],
      loadedDataHash: null,
    });
  }

  shouldUpdateState = ({ changeFlags }) => {
    return changeFlags.somethingChanged;
  };

  getWorldClusters = () => {
    const currentZoom = this.context.viewport.zoom;
    return this.clusterIndex.getWorldClusters(currentZoom);
  };

  getClusterLeaves = (cluster) => {
    const currentZoom = this.context.viewport.zoom;
    return this.clusterIndex.getClusterLeaves(cluster, currentZoom);
  };

  mapCluster = (cluster) => cluster;

  updateState(params) {
    this.debouncedUpdate(params);
  }

  checkDataChange(data) {
    const currentDataHash = this.hashData(data);
    const hasDataChanged = this.state.loadedDataHash !== currentDataHash;

    return {
      hasDataChanged,
      currentDataHash,
    };
  }

  performUpdate({ props: { data }, changeFlags: { viewportChanged } }) {
    const currentZoomLevel = round(this.context.viewport.zoom, 1);
    const dataChange = this.checkDataChange(data);
    const hasZoomChanged = currentZoomLevel !== this.state.zoom;

    const isViewportOrDataChanged = viewportChanged || dataChange.hasDataChanged;
    if (!isViewportOrDataChanged && !hasZoomChanged) {
      return;
    }

    if (dataChange.hasDataChanged || !this.clusterIndex.isReady()) {
      this.clusterIndex.loadData(data);
      this.setState({ loadedDataHash: dataChange.currentDataHash });
    }

    if (dataChange.hasDataChanged || hasZoomChanged) {
      const shouldSkipClustering =
        !!this.props.skipClusteringOnUpdate &&
        !!this.state.data?.length &&
        !dataChange.hasDataChanged &&
        hasZoomChanged;

      if (shouldSkipClustering) {
        this.setState({ zoom: currentZoomLevel });
      } else {
        this.processClusters(currentZoomLevel);
      }
    }

    this.setState({
      zoom: currentZoomLevel,
    });
  }

  hashData = (data) => {
    if (!data.length) return '0';

    const sortedIds = data.map((item) => item.id || item.name || item.properties?.frameId || 'unknown').sort();

    return `${data.length}-${sortedIds[0]}-${sortedIds[sortedIds.length - 1]}`;
  };

  processClusters = (currentZoomLevel) => {
    this.updateStateWithMappedClusters(this.getWorldClusters().map(this.mapCluster), currentZoomLevel);
  };

  updateStateWithMappedClusters = (mappedClusters, currentZoomLevel) => {
    const newState = {
      zoom: currentZoomLevel,
      data: mappedClusters,
    };

    this.setState({ ...newState });
  };

  getPickingInfo({ info, mode }) {
    const pickedObject = info.object && info.object.properties;

    if (!pickedObject) return info;

    info.object = pickedObject;

    if (pickedObject?.cluster && mode !== 'hover') {
      info.objects = pickedObject.assets ?? this.getClusterLeaves(pickedObject).map((leaf) => leaf.properties);
    }

    return info;
  }

  // eslint-disable-next-line no-underscore-dangle
  _finalize() {
    if (this.debouncedUpdate) {
      this.debouncedUpdate.cancel();
    }
    this.clusterIndex?.clear();
    // eslint-disable-next-line no-underscore-dangle
    super._finalize();
  }
}

BaseClusterCompositeLayer.layerName = 'BaseClusterCompositeLayer';
